import {
  Page, Divider, IconButton, IconButtonType, ErrorMessages, Spinner
} from "@whitespace/whitespace-ui";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import useConfig from "hooks/useConfig";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IHwrcSite from "interfaces/IHwrcSite";
import IEditItemOrderRequestModel from "interfaces/Request/IEditItemOrderRequestModel";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  Link, useNavigate, useLocation, useParams
} from "react-router-dom";
import { normaliseWholeNumber } from "modules/Normalise";
import { useStateMachine } from "little-state-machine";

const EditItemOrder = () => {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const config = useConfig();
  const { id, date } = useParams();
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = useForm<IEditItemOrderRequestModel>({
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });
  const location = useLocation();
  const editDetails = { ...location.state as IEditItemOrderRequestModel };

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [editItemOrderButtonDisabled, setEditItemOrderButtonDisabled] = useState<boolean>(true);

  const {
    isSending: editItemOrderIsSending, errors: editItemOrderErrors, statusCode: editItemOrderStatusCode, sendRequest: editItemOrderSendRequest
  } = useAmplifyREST();

  const {
    isSending: siteListIsSending, errors: siteListErrors, data: siteListData, sendRequest: siteListSendRequest
  } = useAmplifyREST();

  const isSending = editItemOrderIsSending || siteListIsSending;
  const apiErrors = editItemOrderErrors || siteListErrors;
  const initialApiLoadComplete = siteListData;

  if (state.editItemOrderPageOrigination!) {
    useEffect(() => {
      siteListSendRequest("settings/hwrcsites/active", "GET");
    }, [siteListSendRequest]);
  }

  const renderHwrcSiteName = () => {
    let siteName : string = "";

    if (siteListData && editDetails) {
      siteName = (siteListData.hwrcSites as IHwrcSite[]).find(hwrcSite => hwrcSite.id === editDetails?.hwrcSiteId)?.name!;
    }

    return siteName;
  };

  const renderOrderTypes = () => {
    const orderTypes = [
      <option key="" value="">Please Select</option>
    ];

    config.itemOrderTypes.forEach(itemType => orderTypes.push(<option key={itemType} value={itemType}>{itemType}</option>));
    return orderTypes;
  };

  const renderStatus = () => {
    const statuses = [
      <option key="" value="">Please Select</option>
    ];

    config.itemOrderStatuses.forEach(status => statuses.push(<option key={status} value={status}>{status}</option>));
    return statuses;
  };

  const watchFields = watch(["orderType", "item", "quantity", "size", "recipient", "status"]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const canFormBeSubmitted = () => {
    const orderType = getValues("orderType");
    const orderTypeCorrect = config.itemOrderTypes.find(x => x === orderType) !== undefined;
    const quantity = getValues("quantity");

    const quantityCorrect = quantity?.toString().length > 0 && Number(quantity) > 0;
    const statusType = getValues("status");
    const statusTypeCorrect = config.itemOrderStatuses.find(x => x === statusType) !== undefined;

    const item = getValues("item");
    const recipient = getValues("recipient");
    const size = getValues("size");
    const sizeCorrect = (editDetails?.size !== null && editDetails?.size !== undefined && editDetails?.size.length > 0
    && size?.localeCompare(editDetails?.size!) !== 0)
    || ((editDetails?.size === null || editDetails?.size === undefined || editDetails?.size?.length === 0) && size?.length! > 0);

    const requiredFieldsEntered = (orderTypeCorrect
    && quantityCorrect
    && item.length > 0
    && recipient.length > 0
   && statusTypeCorrect
    );

    const atLeastOneFieldChangedValue : boolean = (
      (orderTypeCorrect && orderType !== editDetails?.orderType)
      || (item?.length > 0 && item.localeCompare(editDetails?.item!) !== 0)
      || (quantityCorrect && Number(quantity) !== Number(editDetails?.quantity!))
      || sizeCorrect
      || (recipient?.length > 0 && recipient.localeCompare(editDetails?.recipient!) !== 0)
      || (statusTypeCorrect && statusType.localeCompare(editDetails?.status) !== 0)
    );

    setEditItemOrderButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  const handleEditItemOrderClick = (request: IEditItemOrderRequestModel) => {
    setShowExceptions(true);
    request.id = editDetails?.id!;
    editItemOrderSendRequest("itemorders", "PUT", request);
  };

  const handleNavigate = () => {
    if (state.editItemOrderPageOrigination! === RoutePathsEnum.ItemOrders) {
      navigate(state.editItemOrderPageOrigination!);
    } else {
      navigate(`${state.editItemOrderPageOrigination!}/${id}/${date}`);
    }
  };

  useEffect(() => {
    if (editItemOrderStatusCode === 200) {
      handleNavigate();
    }
  }, [editItemOrderStatusCode]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2>
            Edit Item Order
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleEditItemOrderClick)}>
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={12} lg={8} className="d-flex">
                {state.editItemOrderPageOrigination! && (
                  <Breadcrumb className="left-margin-large no-wrap">
                    <Breadcrumb.Item
                      data-testid={state.editItemOrderPageOrigination! === RoutePathsEnum.ItemOrders
                        ? "breadcrumb-item-orders" : "breadcrumb-site-diary"}
                      linkAs={Link}
                      linkProps={{
                        to: state.editItemOrderPageOrigination! === RoutePathsEnum.ItemOrders ? state.editItemOrderPageOrigination!
                          : `${state.editItemOrderPageOrigination!}/${id}/${date}`
                      }}
                      onClick={() => handleNavigate()}
                    >
                      {state.editItemOrderPageOrigination! === RoutePathsEnum.ItemOrders ? "Item Orders" : "Site Diary"}
                    </Breadcrumb.Item>

                    <Breadcrumb.Item data-testid="breadcrumb-edit-item-order" active>
                      Edit Item Order
                    </Breadcrumb.Item>
                  </Breadcrumb>
                ) }
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={editItemOrderButtonDisabled || isSending}
                  aria-disabled={editItemOrderButtonDisabled || isSending}
                  aria-label="Update item order"
                  type="submit"
                  spin={editItemOrderIsSending}
                  data-testid="buttonEditItemOrder"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => handleNavigate()}
                  data-testid="buttonCancel"
                  aria-label={`Cancel edit item order and back to ${state.editItemOrderPageOrigination! === RoutePathsEnum.ItemOrders
                    ? "item orders"
                    : "site diary"}`}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            {initialApiLoadComplete
            && (
              <section className="pt-2">
                <Row className="mt-1 ms-1">
                  <Col xs={12} md={8}>
                    <Row className="mb-2">
                      <Form.Control
                        type="hidden"
                        value={editDetails?.hwrcSiteId!}
                        {...register("hwrcSiteId")}
                      />
                      <Form.Group as={Row} controlId="hwrcSiteId">
                        <Form.Label id="siteLabel" className="horiz-form-label">
                          Site Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="hwrcSiteLabel"
                            maxLength={50}
                            disabled
                            value={renderHwrcSiteName()}
                          />
                        </Col>
                        <ErrorMessages fieldName="hwrcSiteId" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="orderType">
                        <Form.Label
                          id="typeLabel"
                          className="horiz-form-label"
                        >
                          Type:
                        </Form.Label>
                        <Col>
                          <Form.Select
                            {...register("orderType", { onChange: () => canFormBeSubmitted() })}
                            data-testid="ddlOrderType"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="orderTypeLabel"
                            defaultValue={editDetails?.orderType}
                          >
                            {renderOrderTypes()}
                          </Form.Select>
                        </Col>
                        <ErrorMessages
                          fieldName="orderType"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="item">
                        <Form.Label
                          id="itemLabel"
                          className="horiz-form-label"
                        >
                          Item:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="itemLabel"
                            maxLength={50}
                            {...register("item")}
                            defaultValue={editDetails?.item}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="item"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="quantity">
                        <Form.Label
                          id="quantityLabel"
                          className="horiz-form-label"
                        >
                          Quantity:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="quantityLabel"
                            defaultValue={editDetails?.quantity}
                            maxLength={6}
                            {...register("quantity")}
                            onChange={e => { e.target.value = normaliseWholeNumber(e.target.value); setValue("quantity", e.target.value); }}
                            onKeyUp={() => { canFormBeSubmitted(); }}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="quantity"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="size">
                        <Form.Label
                          id="sizeLabel"
                          className="horiz-form-label"
                        >
                          Size Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={false}
                            aria-labelledby="sizeLabel"
                            maxLength={50}
                            {...register("size")}
                            defaultValue={editDetails?.size ?? ""}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="size"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="recipient">
                        <Form.Label
                          id="recipientLabel"
                          className="horiz-form-label"
                        >
                          Recipient:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="recipientLabel"
                            maxLength={50}
                            {...register("recipient")}
                            defaultValue={editDetails?.recipient}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="recipient"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="status">
                        <Form.Label
                          id="statusLabel"
                          className="horiz-form-label"
                        >
                          Status:
                        </Form.Label>
                        <Col>
                          <Form.Select
                            {...register("status")}
                            data-testid="ddlStatus"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="statusLabel"
                            defaultValue={editDetails?.status}
                          >
                            {renderStatus()}
                          </Form.Select>
                          <ErrorMessages
                            fieldName="status"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Col>
                      </Form.Group>
                    </Row>
                  </Col>
                </Row>
              </section>
            )}
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default EditItemOrder;
